import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { RankedCardThumb } from 'components/RankedCardList/RankedCardRow';
import { useGetSeoFormatsQuery, SeoFormatListRow } from 'store/services/seo/publicSeoApi';
import {
  FORMAT_PAGE_KEYS,
  FORMAT_PAGE_LABELS,
  FORMAT_PAGE_TAGS,
  FORMAT_SHORT_COPY,
  FormatPageKey,
} from 'constants/formats';
import './Formats.css';

const SITE = 'https://trademagic.gg';

export const FORMATS_INDEX_SUBTITLE =
  'See which cards traders want in each format, and trade for them. Lists come from live TradeMagic trader data and refresh every night.';

/**
 * /formats (Asana 1219902842494504): eight format cards, each previewing its
 * top three most-wanted cards and its want count from GET /seo/formats, plus a
 * row pointing at /sets. The SEO pieces from Asana 1219287567650303 are unchanged.
 */
const FormatsIndexPage: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { data: formats } = useGetSeoFormatsQuery();
  const byKey = new Map<string, SeoFormatListRow>((formats ?? []).map((f) => [f.formatKey, f]));

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) setIsMobileMenuOpen(false);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const renderCard = (key: FormatPageKey) => {
    const live = byKey.get(key);
    const preview = live?.mostWantedPreview ?? [];
    return (
      <li key={key} className="formats-card" data-testid="formats-card">
        <Link to={`/formats/${key}`} className="formats-card__link">
          <span className="formats-card__head">
            <span className="formats-card__name">{FORMAT_PAGE_LABELS[key]}</span>
            <span className="formats-card__tag">{FORMAT_PAGE_TAGS[key]}</span>
          </span>
          <span className="formats-card__blurb">{FORMAT_SHORT_COPY[key]}</span>
          {preview.length > 0 && (
            <span className="formats-card__preview" data-testid="formats-card-preview">
              <span className="formats-card__preview-label">Most wanted</span>
              {preview.map((row) => (
                <span key={`${row.setCode}-${row.collectorNumber}`} className="formats-card__preview-row">
                  <RankedCardThumb row={row} className="formats-card__preview-thumb" />
                  <span className="formats-card__preview-name">{row.name}</span>
                </span>
              ))}
            </span>
          )}
          <span className="formats-card__foot">
            <span className="formats-card__want">
              {live?.wantUsers !== undefined ? `${live.wantUsers.toLocaleString()} traders want cards` : ''}
            </span>
            <span className="formats-card__view">View &rarr;</span>
          </span>
        </Link>
      </li>
    );
  };

  return (
    <>
      <SEOHelmet
        title="Magic Formats: Most Wanted Cards to Trade | TradeMagic"
        description="The most wanted, most traded and most available cards in Standard, Pioneer, Modern, Legacy, Vintage, Pauper, Commander and Premodern, from live TradeMagic trader data."
        canonical={`${SITE}/formats`}
        type="website"
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: `${SITE}/` },
          { name: 'Formats', url: `${SITE}/formats` },
        ]}
      />

      <div className="formats-layout">
        {isMobile && isMobileMenuOpen && (
          <div className="formats-mobile-overlay" onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
        )}
        <div className="formats-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/formats', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={() => { if (!currentUser) setShowLoginModal(true); }}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="formats-main">
            <TopNavigationBar
              title="Formats"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={() => setIsMobileMenuOpen((o) => !o)}
            />
            <div className="formats-container">
              <header className="formats-header">
                <h1 className="formats-title">Formats</h1>
                <p className="formats-muted">{FORMATS_INDEX_SUBTITLE}</p>
              </header>
              <ul className="formats-grid" data-testid="formats-grid">
                {FORMAT_PAGE_KEYS.map(renderCard)}
              </ul>
              <div className="formats-sets-row">
                <span>Looking for a specific set instead?</span>
                <Link to="/sets" className="formats-sets-row__link">Browse all sets &rarr;</Link>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />
    </>
  );
};

export default FormatsIndexPage;
